import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Badge, CreativeStatusBadge } from '../Badges'
import { DEBOUNCE_TIMEOUT, TIMEOUT_AFTER_SUCCESSFUL_CALLBACK } from 'constants/application'
import { bigFromFee, decimalFromBig, formatCurrency, formatPrice } from 'utils/price'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import BlockInfo from '../BlockInfo/BlockInfo'
import Button from '../Button/Button'
import { CreativeEligibilityOverride } from '../CreativeEligibilityOverride'
import Icon from '../Icon/Icon'
import { IconType } from 'constants/assets'
import { IneligibleCreativeSelectionDTO, SuggestedCreativeDTO } from 'models/creative'
import { Nullable } from 'models/helpers'
import { QueryStatus, QueryStatusStateVariant } from '../QueryStatus'
import { debounceEffect } from 'utils/helpers'
import inputStyles from '../../styles/input.module.sass'
import { isIneligibleCreativeSelectionError } from 'utils/typeguards'
import { logger } from 'utils/logging'
import { multilineText } from 'utils/formatting'
import styles from '../AssignCreative/AssignCreative.module.sass'
import { useAdminSelectCreative } from 'dataQueries'
import { useGalleryDeal } from 'components/pages/Gallery/_main/contexts/GalleryDeal.context'
import { useTranslation } from 'react-i18next'
import { useFormatterLanguage } from 'utils/hooks/useFormatterLanguage'

/**
 * States QueryStatus renders while the eligibility override panel is on screen. The rejection is
 * already fully explained by the panel, and the generic strip would otherwise print
 * `undefined - <message> (undefined)` on top of it — the typed 422 body carries no `status` or
 * `timestamp`. Module-level constant so the array identity stays stable across renders
 * (QueryStatus takes `showStates` as an effect dependency).
 */
const QUERY_STATUS_STATES_WITHOUT_ERROR: QueryStatusStateVariant[] = ['success', 'pending']

/**
 * @interface Props Input properties
 */
export interface Props {
  /** The additional classes to append */
  className?: string
  /** Assignment ID */
  assignmentId: string
  /** Data of the chosen creative */
  creativeData: SuggestedCreativeDTO
  /** On button "Back" click action to be called */
  onClickBack?: (e: React.MouseEvent) => void
  /** Function called after creative selected with successful response */
  callbackAfterSuccessfulResponse?: () => void
}

/**
 * @component Assign chosen creative detail for admin
 * @example
 * <AssignChosenCreativeDetail assignmentId={assignmentId} creativeData={SuggestedCreative} />
 */
const AssignChosenCreativeDetail: React.FC<Props> = ({
  className = '',
  assignmentId,
  creativeData,
  onClickBack,
  callbackAfterSuccessfulResponse,
}) => {
  const { t } = useTranslation(['assign_creative'])
  const { dealId } = useGalleryDeal()

  const adminSelectCreative = useAdminSelectCreative()

  const initRemuneration = useMemo(() => bigFromFee(creativeData.remuneration || 0), [creativeData])
  const initTransportCost = useMemo(() => bigFromFee(creativeData.transportRemuneration || 0), [creativeData])
  const initBonus = useMemo(() => bigFromFee(creativeData.bonus || 0), [creativeData])
  const initTotalRemuneration = useMemo(() => bigFromFee(initRemuneration.plus(initTransportCost).plus(initBonus)), [initRemuneration, initTransportCost, initBonus])
  const initMargin = useMemo(() => bigFromFee(creativeData.margin || 0), [creativeData])
  const currency = useMemo(() => creativeData.remuneration.currency, [creativeData])
  const formatterLanguage = useFormatterLanguage()
  const formattedCurrency = useMemo(() => formatCurrency(currency, formatterLanguage), [currency, formatterLanguage])
  const availabilityStatus = useMemo(() => creativeData.availabilityStatus, [creativeData])


  // Editable input values, not display labels: no currency is passed, so `formatPrice` returns a plain
  // fixed-decimal string (`Big.toFixed(2)`) without touching Intl — they are not locale-formatted and
  // need no `formatterLanguage`. The currency-bearing labels below format inline in JSX and re-render.
  const [transportCostValue, setTransportCostValue] = useState(formatPrice(initTransportCost))
  const [transportCostDifference, setTransportCostDifference] = useState(formatPrice(0))
  const [bonusValue, setBonusValue] = useState(formatPrice(initBonus))
  const [bonusDifference, setBonusDifference] = useState(formatPrice(0))
  const [remunerationValue, setRemunerationValue] = useState(formatPrice(initRemuneration))
  const [remunerationDifference, setRemunerationDifference] = useState(formatPrice(0))
  const [totalRemunerationValue, setTotalRemunerationValue] = useState(formatPrice(initTotalRemuneration))
  const [marginValue, setMarginValue] = useState(formatPrice(initMargin))
  const [overrideReason, setOverrideReason] = useState('')

  /**
   * The eligibility rejection (ISSUE-2946), or null before one happens. Deliberately narrowed by
   * payload shape: a blacklisted creative answers with the same 422 status but an untyped body, and
   * must keep falling through to the generic error strip rather than offering an override.
   *
   * Latched in state rather than derived from `adminSelectCreative.error`, because React Query
   * clears `error` to null the moment the override retry goes pending — reading it live would
   * unmount the rule list and the reason field mid-submit, and leave them gone for good if the
   * retry then failed for an unrelated reason, while the typed reason stayed attached to every
   * later submit. Cleared only on success.
   */
  const [ineligibleSelection, setIneligibleSelection] = useState<Nullable<IneligibleCreativeSelectionDTO>>(null)

  useEffect(() => {
    if (isIneligibleCreativeSelectionError(adminSelectCreative.error)) {
      const refusal = adminSelectCreative.error.response?.data ?? null
      setIneligibleSelection(refusal)

      // The denominator of the override funnel. Without it production sees only the overrides that
      // completed and never the admins who were blocked — including the ones who give up and click
      // Back, which is the exact regression this ticket exists to prevent and leaves no other trace
      // (Sentry is deliberately suppressed here, and the backend audit row is written only on success).
      // Rule codes and categories only: `overrideReason` is admin free text and never leaves the body.
      if (refusal) {
        const failedRules = refusal.ineligibleAssignments.flatMap((entry) => entry.failedRules)

        logAnalyticsEvent(AnalyticsEvent.CREATIVE_SELECTION_REFUSED_BY_ELIGIBILITY, {
          assignmentId,
          creativeId: creativeData.id,
          ruleCodes: failedRules.map((rule) => rule.code),
          ruleCategories: failedRules.map((rule) => rule.category),
        })
      }
    } else if (adminSelectCreative.isSuccess) {
      setIneligibleSelection(null)
      setOverrideReason('')
    }
  }, [adminSelectCreative.error, adminSelectCreative.isSuccess, assignmentId, creativeData.id])

  const isOverrideRequired = !!ineligibleSelection
  const isOverrideReasonMissing = isOverrideRequired && !overrideReason.trim()

  const debounceTotalRemunerationTimeoutRef = useRef<number | undefined>(undefined)
  const firstDebounceOccured = useRef<boolean>(false)

  const isCreativeTransportCostEmpty = useMemo(() => transportCostValue === '', [transportCostValue])
  const isCreativeBonusEmpty = useMemo(() => bonusValue === '', [bonusValue])

  useEffect(() => {
    debounceEffect(() => {
      if (transportCostValue && bonusValue) {
        setTransportCostDifference(formatPrice(bigFromFee(initTransportCost).minus(bigFromFee(transportCostValue))))
        setBonusDifference(formatPrice(bigFromFee(initBonus).minus(bigFromFee(bonusValue))))
        setRemunerationDifference(formatPrice(bigFromFee(initRemuneration).minus(bigFromFee(remunerationValue))))
        setMarginValue(formatPrice(bigFromFee(initMargin).plus(bigFromFee(transportCostDifference)).plus(bigFromFee(bonusDifference).plus(remunerationDifference))))
        setTotalRemunerationValue(formatPrice(bigFromFee(remunerationValue).plus(bigFromFee(transportCostValue)).plus(bigFromFee(bonusValue))))
      }
    }, debounceTotalRemunerationTimeoutRef, firstDebounceOccured.current ? DEBOUNCE_TIMEOUT : 0)
  }, [initBonus, initTransportCost, remunerationValue, initMargin, transportCostValue, transportCostDifference, bonusValue, bonusDifference, initRemuneration, remunerationDifference])


  // Held in a ref so the auto-close effect can depend on `isSuccess` alone. The callback arrives as a
  // fresh arrow on every ancestor render (CreativeAssignmentCard passes an inline closure), so keeping
  // it in the dependency list would clear and requeue the timer on each render — the popup would close
  // 3s after the LAST render rather than 3s after success, and never at all under a sustained cadence.
  const callbackAfterSuccessfulResponseRef = useRef(callbackAfterSuccessfulResponse)
  callbackAfterSuccessfulResponseRef.current = callbackAfterSuccessfulResponse

  // Keyed on `isSuccess`, not on the mutation object: the latter is a new reference every render, so
  // typing in the override reason would otherwise queue one uncleaned timer per keystroke
  useLayoutEffect(() => {
    if (!adminSelectCreative.isSuccess) return

    const timeout = window.setTimeout(() => callbackAfterSuccessfulResponseRef.current?.(), TIMEOUT_AFTER_SUCCESSFUL_CALLBACK)

    return () => window.clearTimeout(timeout)
  }, [adminSelectCreative.isSuccess])

  const bookCreative = useCallback(() => {
    // Nothing to resubmit until the mandatory justification is typed. Reaching this at all means the
    // button's disabled state and this guard disagreed, which is a real desync worth seeing — the bug
    // report otherwise reads "the confirm button does nothing" with no trace anywhere. Unreachable in
    // normal operation, so it adds no steady-state noise.
    if (isOverrideReasonMissing) {
      logger.warn('Creative selection submitted with no override reason despite the gate', undefined, {
        context: {
          component: 'AssignChosenCreativeDetail',
          action: 'bookCreative',
          metadata: { assignmentId, creativeId: creativeData.id },
        },
      })
      return
    }

    let creativeRemuneration = creativeData.remuneration
    creativeRemuneration.value = decimalFromBig(remunerationValue)
    let creativeTransportRemuneration = creativeData.transportRemuneration
    creativeTransportRemuneration.value = decimalFromBig(transportCostValue)
    let creativeBonus = creativeData.bonus
    creativeBonus.value = decimalFromBig(bonusValue)

    // The override retry is the identical request plus the reason — the backend persists nothing
    // when it rejects, so re-sending the same remuneration is safe
    adminSelectCreative.mutate({
      dealId,
      assignmentId,
      creativeId: creativeData.id,
      remuneration: creativeRemuneration,
      transportRemuneration: creativeTransportRemuneration,
      bonus: creativeBonus,
      overrideReason: overrideReason.trim() || undefined,
    })
  }, [adminSelectCreative, assignmentId, bonusValue, creativeData.bonus, creativeData.id, creativeData.remuneration, creativeData.transportRemuneration, dealId, isOverrideReasonMissing, overrideReason, remunerationValue, transportCostValue])

  return (
    <div className={className}>
      <div className={`${styles.line} ${styles.badges}`}>
        <CreativeStatusBadge
          className={`${styles.creativeBadge} ${styles.marginRight}`}
          creativeStatus={availabilityStatus}
        />
        <Badge color={(parseInt(marginValue) >= 0) ? 'gray' : 'red'}>
          <strong className={`${styles.total} ${styles.marginRight}`}>{t('margin')}</strong>
          <strong className={(parseInt(marginValue) >= 0) ? `${styles.black}` : ''}>{formatPrice(marginValue, currency)}</strong>
        </Badge>
      </div>
      <div className={styles.line}>
        <div className={styles.nameWrapper}>
          <span className={`${styles.name} ${styles.marginRight}`}>{t('name')}</span>
          <strong className={`${styles.title} ${styles.black}`}>{creativeData.name}</strong>
        </div>
      </div>
      <div className={`${styles.line} ${styles.detail} ${styles.marginVertical}`}>
        <div className={styles.contactWrapper}>
          {!!creativeData.phone &&
            <span className={styles.group}>
              <a href={`tel:${creativeData.phone}`}>
                <Icon className={styles.smallIcon} icon={IconType.PHONE} />
                <span>{creativeData.phone}</span>
              </a>
            </span>
          }
          <span className={styles.group}>
            <span className={styles.separateLeft}>
              <a href={`mailto:${creativeData.email}`}>
                <Icon className={styles.smallIcon} icon={IconType.ENVELOPE} />
                <span>{creativeData.email}</span>
              </a>
            </span>
          </span>
        </div>
      </div>
      <div className={`${styles.line} ${styles.marginTopLarge}`}>
        <strong className={`${styles.total} ${styles.marginRight}`}>{t('total_remuneration')}</strong>
        <strong className={styles.black}>{formatPrice(totalRemunerationValue, currency)}</strong>
      </div>
      <div className={`${styles.line} ${styles.detail}`}>
        <span className={styles.marginRight}>{t('remuneration')}</span>
        <div className={`input-group ${inputStyles.inputGroup}`}>
          <input
            className={`${inputStyles.numberInput} ${styles.marginRight}`}
            type="number"
            name="remuneration"
            id="remuneration"
            value={remunerationValue}
            min="0"
            step="0.01"
            placeholder={t('number')}
            onWheel={e => e.currentTarget.blur()}
            onChange={e => setRemunerationValue(formatPrice(e.target.value))}
          />
          <strong>{formattedCurrency}</strong>
        </div>
      </div>
      <div className={`${styles.line} ${styles.detail}`}>
        <span className={styles.marginRight}>{t('transport_cost')}</span>
        <div className={`input-group ${inputStyles.inputGroup}`}>
          <input
            className={`${inputStyles.numberInput} ${styles.marginRight} ${isCreativeTransportCostEmpty ? 'error-input' : ''}`.trim()}
            type="number"
            name="transport-cost"
            id="transport-cost"
            value={transportCostValue}
            min="0"
            step="0.01"
            placeholder={t('number')}
            onWheel={e => e.currentTarget.blur()}
            onChange={e => setTransportCostValue(formatPrice(e.target.value))}
          />
          <strong>{formattedCurrency}</strong>
          {isCreativeTransportCostEmpty &&
            <span className={`error-message ${inputStyles.errorMessage}`}>{t('field_empty')}</span>
          }
        </div>
      </div>
      <div className={`${styles.line} ${styles.detail} ${styles.marginBottom}`}>
        <span className={styles.marginRight}>{t('bonus')}</span>
        <div className={`input-group ${inputStyles.inputGroup}`}>
          <input
            className={`${inputStyles.numberInput} ${styles.marginRight} ${isCreativeBonusEmpty ? 'error-input' : ''}`.trim()}
            type="number"
            name="bonus"
            id="bonus"
            value={bonusValue}
            min="0"
            step="0.01"
            placeholder={t('number')}
            onWheel={e => e.currentTarget.blur()}
            onChange={e => setBonusValue(formatPrice(e.target.value))}
          />
          <strong>{formattedCurrency}</strong>
          {isCreativeBonusEmpty &&
            <span className={`error-message ${inputStyles.errorMessage}`}>{t('field_empty')}</span>
          }
        </div>
      </div>
      <div className={`${styles.line} ${styles.marginTopLarge}`}>
        <strong>{t('admin_comments_about_creative')}</strong>
      </div>
      <BlockInfo className={styles.blockinfo} color="blue">
        {multilineText(creativeData.adminComments || '')}
      </BlockInfo>

      {!!ineligibleSelection &&
        <CreativeEligibilityOverride
          ineligibleAssignments={ineligibleSelection.ineligibleAssignments}
          overrideReason={overrideReason}
          isDisabled={adminSelectCreative.isPending || adminSelectCreative.isSuccess}
          onChangeOverrideReason={setOverrideReason}
        />
      }

      <div className={`${styles.line} ${styles.marginTopLarge}`}>
        <div className={styles.buttonsWrapper}>
          <Button
            type="secondary"
            className={`${styles.book} ${styles.marginRight}`}
            onClick={() => bookCreative()}
            disabled={adminSelectCreative.isPending || adminSelectCreative.isSuccess || isOverrideReasonMissing}
          >
            {isOverrideRequired ? t('confirm_override') : t('validate_and_book')}
          </Button>
          <Button
            type="secondary red"
            className={styles.back}
            onClick={onClickBack}
            disabled={adminSelectCreative.isPending || adminSelectCreative.isSuccess}
          >
            {t('back')}
          </Button>
        </div>
      </div>

      <QueryStatus
        query={adminSelectCreative}
        successMessage={t('success')}
        spaceTopRem={2}
        // Live-derived, not latched: only the eligibility rejection is fully explained by the panel.
        // A 500 or a blacklist 422 on the override retry must still surface the generic strip.
        showStates={isIneligibleCreativeSelectionError(adminSelectCreative.error) ? QUERY_STATUS_STATES_WITHOUT_ERROR : undefined}
      />

    </div>
  )
}

export default AssignChosenCreativeDetail
